import { from, Observable, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@nrwl/environment';
import { AuthService } from '@nrwl/rx-service-auth';
import { Form } from '@nrwl/rx-models';
import { FormRequestOptions, withFormType } from '../form-query/form-query-params.utils';

interface CarryForwardFormData {
  publicationDate: string;
  appointmentId?: string;
  treatmentId?: string;
}

interface CarryForwardFormRequest {
  publication_date: string;
  appointment_id?: string;
  treatment_id?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FormService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  public getForm() {
    // RWE-2527: FORMS v2 to v3
    return this.http.get(environment.baseAPIUrlv3 + 'form/');
  }

  public load(): Observable<any> {
    // RWE-2527: FORMS v2 to v3
    return this.http.get<any>(environment.baseAPIUrlv3 + `form/`);
  }

  public loadV3(): Observable<any> {
    return this.http.get<any>(environment.baseAPIUrlv3 + `form/`);
  }

  public getById(id: number): Observable<any> {
    // RWE-2527: FORMS v2 to v3
    return this.http.get<any>(environment.baseAPIUrlv3 + `form/${id}/?expand=author`);
  }

  public getByIdV3(id: number, includeArchived = false): Observable<any> {
    const queryParams = { expand: 'author' };

    if (includeArchived) {
      queryParams['all'] = true;
    }

    return this.http.get<any>(environment.baseAPIUrlv3 + `form/${id}/`, { params: queryParams });
  }

  public create(data: any): Observable<any> {
    // Original Angular HttpClient method (commented out)
    /*
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
        });
        return this.http.post<any>(environment.baseAPIUrlv3 + `form/`, data, {
          headers,
        });
        */

    const url = environment.baseAPIUrlv3 + `form/`;
    const body = JSON.stringify(data);

    const fetchPromise = fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${this.authService.getToken()}`,
      },
      body: body,
    })
      .then(async (response) => {
        if (!response.ok) {
          let errorBody: unknown = null;

          try {
            errorBody = await response.json();
          } catch {
            errorBody = null;
          }

          throw {
            status: response.status,
            error: errorBody,
            message: `HTTP error! status: ${response.status}`,
          };
        }
        return response.json();
      })
      .catch((error) => {
        // Handle errors
        console.error('Fetch Error:', error);
        throw error;
      });

    return from(fetchPromise);
  }

  public update(formId: number, data: any): Observable<any> {
    const url = environment.baseAPIUrlv3 + `form/${formId}/`;

    let bodyData = data;
    if (data && data.form_data && data.metadata) {
      bodyData = {
        form_data: data.form_data,
        metadata: data.metadata,
        status: data.status,
        shared_with_patient: data.shared_with_patient,
        patient: data.patient,
        template: data.template,
      };
    }

    const body = JSON.stringify(bodyData);

    const fetchPromise = fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${this.authService.getToken()}`,
      },
      body: body,
    })
      .then(async (response) => {
        if (!response.ok) {
          let errorBody: unknown = null;

          try {
            errorBody = await response.json();
          } catch {
            errorBody = null;
          }

          throw {
            status: response.status,
            error: errorBody,
            message: `HTTP error! status: ${response.status}`,
          };
        }
        return response.json();
      })
      .catch((error) => {
        console.error('Fetch Error:', error);
        throw error;
      });

    return from(fetchPromise);
  }

  /** Replaces instance data; formData must include the full structure and existing answers. */
  public customize(formId: number, formData: string): Observable<Form> {
    return this.http.post<Form>(environment.baseAPIUrlv3 + `form/${formId}/customize/`, {
      form_data: formData,
    });
  }

  public postBulk(data: any): Observable<any> {
    return this.http.post<any>(environment.baseAPIUrlv3 + `form/bulk/`, data);
  }

  public updateBulk(data: any, options: FormRequestOptions = {}): Observable<Form[]> {
    const params = withFormType(new HttpParams(), options);

    return this.http.patch<Form[]>(environment.baseAPIUrlv3 + `form/bulk/`, data, { params });
  }

  public carryForward(formId: number, data: CarryForwardFormData): Observable<any> {
    const body: CarryForwardFormRequest = {
      publication_date: data.publicationDate,
    };

    if (data.appointmentId != null) {
      body.appointment_id = data.appointmentId;
    }

    if (data.treatmentId != null) {
      body.treatment_id = data.treatmentId;
    }

    return this.http.post<any>(environment.baseAPIUrlv3 + `form/${formId}/carry-forward/`, body);
  }

  public restore(formId: number, data: any): Observable<any> {
    return this.http.post<any>(
      environment.baseAPIUrlv3 + ` /api/v3/form/${formId}/unarchive/`,
      data,
    );
  }

  public restoreBulk(ids: number[]): Observable<any> {
    return this.http.post<any>(environment.baseAPIUrlv3 + `form/bulk/unarchive/`, { ids });
  }

  public delete(formId: number): Observable<any> {
    return this.http.delete<any>(environment.baseAPIUrlv3 + `form/${formId}/`);
  }

  public deleteBulk(ids: string, options: FormRequestOptions = {}): Observable<any> {
    let params = withFormType(new HttpParams(), options);
    params = params.set('ids', ids);
    return this.http.delete<any>(environment.baseAPIUrlv3 + `form/bulk/`, {
      params,
    });
  }

  public loadV3Next(url = environment.baseAPIUrlv3 + 'form/', result: any = []) {
    return new Promise<any>((resolve, reject) => {
      this.http
        .get(url)
        .pipe(
          take(1),
          catchError((error) => {
            console.log('ERROR: Load forms', error);
            return of({});
          }),
        )
        .subscribe((data: any) => {
          if (data && data.next) {
            this.loadV3Next(data.next, [...data.results, ...result]).then((res) => {
              resolve(res);
            });
          } else {
            const res = [...result, ...(data.results || [])];
            resolve(res);
          }
        });
    });
  }
}
